/**
 * The object property panel (10-web-admin § 6.4).
 *
 * One panel, with the sections each kind has: the desk is the one the board
 * draws; rooms, lockers, parking spots and offices differ in fields, not in
 * shape. There is deliberately **no price field** on any object — prices are
 * product data (D-051) — and no assignment field, because a PrivateDesk is
 * assigned through a contract (D-082), never here.
 */

import { useState, type RefObject } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner } from '@cowoo/shared/ui-web';
import {
  ApiProblem,
  CAPACITY_RANGE,
  clampSize,
  MAX_ADVANCE_DAYS,
  MAX_ATTRIBUTE_LENGTH,
  MAX_ATTRIBUTES,
  rotateObject,
  validateLabel,
  type BookableBy,
  type EditorObject,
  type MaxAdvanceDays,
} from '@cowoo/shared';
import { api } from '../../lib/api';
import { Icon, RadioCard, Segmented, fieldClass } from '../../ui';

export interface ObjectPanelProps {
  object: EditorObject;
  all: EditorObject[];
  /** § 6.4 "Fest zugewiesen an" — read-only, from the contract. */
  assignedTo: string | null;
  bookingsToday: number;
  bookingsUpcoming: number;
  /** § 6.3 — `Enter` and the rail's pencil focus "Nummer". */
  labelRef?: RefObject<HTMLInputElement | null>;
  onChange: (next: EditorObject) => void;
  onRemove: () => void;
}

type StatusChoice = EditorObject['status'] | 'maintenance';

export function ObjectPanel({
  object,
  all,
  assignedTo,
  bookingsToday,
  bookingsUpcoming,
  labelRef,
  onChange,
  onRemove,
}: ObjectPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const labelProblem = validateLabel(object.label, all, object.resourceId);
  const zones = [...new Set(all.map((item) => item.zone).filter((zone): zone is string => !!zone))];

  const [status, setStatus] = useState<StatusChoice>(object.status);
  const [fromDay, setFromDay] = useState(new Date().toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [newTag, setNewTag] = useState<string | null>(null);

  /**
   * § 6.4 — "Wartung, mit Zeitraum" is a `resource_block`, not a placement
   * status: blocks are version-independent and take effect the moment they
   * are confirmed, not at publish. The panel says so beside the button.
   */
  const maintenance = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/resource-blocks', {
        body: { resourceId: object.resourceId, type: 'maintenance', fromDay, toDay, reason: reason.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'resource-blocks'] });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const chooseStatus = (next: StatusChoice): void => {
    setStatus(next);
    if (next !== 'maintenance') onChange({ ...object, status: next });
  };

  const bookableBy = (value: string): void => {
    const map: Record<string, BookableBy[]> = {
      both: ['flex_desk', 'day_ticket'],
      flex_desk: ['flex_desk'],
      day_ticket: ['day_ticket'],
      none: [],
    };
    onChange({ ...object, bookableBy: map[value] ?? [] });
  };
  const bookableByValue =
    object.bookableBy.length === 2 ? 'both' : object.bookableBy.length === 0 ? 'none' : object.bookableBy[0]!;

  const kind = object.kind;
  const removeLabel = t(`admin.editor.remove.${kind}`);
  const removable = assignedTo === null;

  return (
    <>
      {/* ------------------------------ header ------------------------------ */}
      <div className="border-line flex items-center justify-between gap-3 border-b px-[18px] pb-3 pt-4">
        <div className="min-w-0">
          <p className="font-heading text-ink truncate text-lg font-bold">
            {kind === 'desk' ? t('admin.editor.panelDeskTitle', { label: object.label }) : object.label}
          </p>
          <p className="font-body text-muted text-xs">
            {[
              object.zone,
              kind === 'desk'
                ? t(object.deskType === 'private' ? 'admin.editor.panelPrivate' : 'admin.editor.panelFlex')
                : t(`admin.editor.group.${kind}`),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <span
          className={`font-body inline-flex h-[30px] flex-none items-center rounded-full px-2.5 text-[13px] font-semibold ${
            object.status === 'bookable' ? 'bg-teal-tint text-teal-dark' : 'bg-fog text-muted'
          }`}
        >
          {t(object.status === 'bookable' ? 'admin.editor.panelBookable' : 'admin.editor.panelBlocked')}
        </span>
      </div>

      {/* ------------------------------- body ------------------------------- */}
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-3.5">
        {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

        <div className="grid grid-cols-2 gap-2.5">
          <Labelled
            label={
              kind === 'room' || kind === 'office'
                ? t('admin.editor.panelName')
                : t('admin.editor.panelLabel')
            }
          >
            <input
              ref={labelRef}
              value={object.label}
              maxLength={12}
              onChange={(event) => onChange({ ...object, label: event.target.value })}
              className={`${fieldClass} tabular-nums`}
              aria-invalid={labelProblem !== null || undefined}
            />
          </Labelled>
          <Labelled label={t('admin.editor.panelZone')}>
            <input
              list="editor-zones"
              value={object.zone ?? ''}
              onChange={(event) => onChange({ ...object, zone: event.target.value || null })}
              className={fieldClass}
            />
            <datalist id="editor-zones">
              {zones.map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </Labelled>
        </div>
        {labelProblem ? (
          <p className="text-adminSmall text-danger-text -mt-2">{t(LABEL_ERRORS[labelProblem])}</p>
        ) : kind === 'desk' ? (
          // BR-PLAN-02 — the QR is bound to the resource, so renaming costs a
          // reprint and nothing else.
          <p className="text-adminSmall text-muted -mt-2 leading-normal">{t('admin.editor.relabelHint')}</p>
        ) : null}

        {kind === 'desk' ? (
          <Labelled label={t('admin.editor.panelType')}>
            <Segmented
              label={t('admin.editor.panelType')}
              value={object.deskType ?? 'flex'}
              onChange={(deskType) =>
                onChange({
                  ...object,
                  deskType,
                  // § 6.4 — a PrivateDesk is nobody's to book; it is assigned.
                  bookableBy: deskType === 'private' ? [] : object.bookableBy,
                })
              }
              options={[
                { value: 'flex', label: t('admin.editor.panelFlex') },
                { value: 'private', label: t('admin.editor.panelPrivate') },
              ]}
            />
          </Labelled>
        ) : null}

        {kind === 'room' || kind === 'office' ? (
          <Labelled label={t('admin.editor.panelCapacity')}>
            <input
              type="number"
              min={CAPACITY_RANGE.min}
              max={kind === 'office' ? 6 : CAPACITY_RANGE.max}
              value={object.capacity ?? ''}
              onChange={(event) =>
                onChange({
                  ...object,
                  capacity: event.target.value === '' ? null : Number(event.target.value),
                })
              }
              className={`${fieldClass} tabular-nums`}
            />
          </Labelled>
        ) : null}

        {kind !== 'parking' && kind !== 'office' ? (
          <Labelled label={t('admin.editor.panelStatus')}>
            <div className="flex flex-col gap-1.5">
              <RadioCard
                name="status"
                value="bookable"
                checked={status === 'bookable'}
                onChange={() => chooseStatus('bookable')}
                mark={<span className="border-teal h-3 w-3 flex-none rounded-[4px] border-2 bg-white" />}
              >
                {t('admin.editor.panelBookable')}
              </RadioCard>
              <RadioCard
                name="status"
                value="blocked"
                checked={status === 'blocked'}
                onChange={() => chooseStatus('blocked')}
                mark={<span className="bg-disabled h-3 w-3 flex-none rounded-[4px]" />}
              >
                {t('admin.editor.panelBlocked')}
              </RadioCard>
              {kind !== 'locker' ? (
                <RadioCard
                  name="status"
                  value="maintenance"
                  checked={status === 'maintenance'}
                  onChange={() => chooseStatus('maintenance')}
                  mark={
                    <span className="bg-warn-bg border-warn-dot h-3 w-3 flex-none rounded-[4px] border-2" />
                  }
                >
                  {t('admin.editor.panelMaintenance')}
                </RadioCard>
              ) : null}
            </div>
          </Labelled>
        ) : null}

        {kind === 'desk' || kind === 'room' ? (
          // The board draws Von/Bis under the status rows; they are live only
          // while "Wartung, mit Zeitraum" is chosen.
          <div className="grid grid-cols-2 gap-2.5">
            <Labelled label={t('admin.plans.blockFrom')}>
              <input
                type="date"
                value={fromDay}
                disabled={status !== 'maintenance'}
                onChange={(e) => setFromDay(e.target.value)}
                className={`${fieldClass} tabular-nums`}
              />
            </Labelled>
            <Labelled label={t('admin.plans.blockTo')}>
              <input
                type="date"
                value={toDay}
                disabled={status !== 'maintenance'}
                onChange={(e) => setToDay(e.target.value)}
                className={`${fieldClass} tabular-nums`}
              />
            </Labelled>
          </div>
        ) : null}
        {status === 'maintenance' ? (
          <div className="flex flex-col gap-2.5">
            <Labelled label={t('admin.plans.blockReason')}>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('admin.reasonPlaceholder')}
                className={fieldClass}
              />
            </Labelled>
            <button
              type="button"
              disabled={reason.trim().length < 5 || toDay < fromDay || maintenance.isPending}
              onClick={() => maintenance.mutate()}
              className="bg-teal text-on-teal rounded-input disabled:bg-disabled disabled:text-placeholder h-10 text-sm font-semibold"
            >
              {t('admin.editor.maintenanceApply')}
            </button>
            <p className="text-adminSmall text-muted leading-normal">{t('admin.editor.maintenanceNote')}</p>
          </div>
        ) : null}

        {kind !== 'parking' ? (
          <Labelled label={t('admin.editor.panelAttributes')}>
            <div className="flex flex-wrap gap-1.5">
              {object.attributes.map((tag) => (
                <span
                  key={tag}
                  className="bg-teal-tint text-teal-dark font-body inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[13px] font-semibold"
                >
                  {tag}
                  <button
                    type="button"
                    aria-label={`${t('common.delete')} ${tag}`}
                    onClick={() =>
                      onChange({ ...object, attributes: object.attributes.filter((item) => item !== tag) })
                    }
                    className="hover:text-ink"
                  >
                    <Icon name="close" size={11} />
                  </button>
                </span>
              ))}
              {newTag !== null ? (
                <input
                  ref={(node) => node?.focus()}
                  value={newTag}
                  maxLength={MAX_ATTRIBUTE_LENGTH}
                  onChange={(e) => setNewTag(e.target.value)}
                  onBlur={() => setNewTag(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTag.trim()) {
                      onChange({ ...object, attributes: [...object.attributes, newTag.trim()] });
                      setNewTag('');
                    }
                    if (e.key === 'Escape') setNewTag(null);
                  }}
                  className="border-line h-7 rounded-full border px-2.5 text-[13px]"
                />
              ) : (
                <button
                  type="button"
                  disabled={object.attributes.length >= MAX_ATTRIBUTES}
                  onClick={() => setNewTag('')}
                  className="border-placeholder text-muted font-body inline-flex h-7 items-center rounded-full border border-dashed px-2.5 text-[13px] font-semibold disabled:opacity-50"
                >
                  {t('admin.editor.panelAddAttribute')}
                </button>
              )}
            </div>
          </Labelled>
        ) : null}

        {kind === 'desk' || kind === 'room' ? (
          <Labelled label={t('admin.editor.panelRules')}>
            <div className="flex flex-col gap-1.5 text-[13px]">
              {kind === 'desk' ? (
                <RuleRow label={t('admin.editor.panelBookableBy')}>
                  <select
                    value={bookableByValue}
                    onChange={(e) => bookableBy(e.target.value)}
                    className={smallFieldClass}
                  >
                    <option value="both">{t('admin.editor.bookableByBoth')}</option>
                    <option value="flex_desk">{t('admin.editor.panelFlex')}</option>
                    <option value="day_ticket">{t('admin.editor.bookableByDay')}</option>
                    <option value="none">{t('admin.editor.nobody')}</option>
                  </select>
                </RuleRow>
              ) : null}
              <RuleRow label={t('admin.editor.panelAdvance')}>
                <select
                  value={object.maxAdvanceDays}
                  onChange={(e) =>
                    onChange({ ...object, maxAdvanceDays: Number(e.target.value) as MaxAdvanceDays })
                  }
                  className={`${smallFieldClass} tabular-nums`}
                >
                  {MAX_ADVANCE_DAYS.map((days) => (
                    <option key={days} value={days}>
                      {t('admin.editor.advanceDays', { count: days })}
                    </option>
                  ))}
                </select>
              </RuleRow>
              {kind === 'desk' ? (
                <RuleRow label={t('admin.editor.panelAssigned')}>
                  <span className={`${smallFieldClass} text-placeholder flex items-center`}>
                    {assignedTo ?? t('admin.editor.nobody')}
                  </span>
                </RuleRow>
              ) : (
                <p className="text-adminSmall text-muted leading-normal">{t('admin.editor.roomRule')}</p>
              )}
            </div>
          </Labelled>
        ) : null}

        {kind === 'locker' ? (
          <RuleRow label={t('admin.editor.lockerAssigned')}>
            <span className={`${smallFieldClass} text-placeholder flex items-center`}>
              {assignedTo ?? t('admin.editor.lockerFree')}
            </span>
          </RuleRow>
        ) : null}
        {kind === 'office' ? (
          <>
            <RuleRow label={t('admin.editor.officeRented')}>
              <span className={`${smallFieldClass} text-placeholder flex items-center`}>
                {assignedTo ?? t('admin.editor.lockerFree')}
              </span>
            </RuleRow>
            <p className="text-adminSmall text-muted leading-normal">{t('admin.editor.officeRule')}</p>
          </>
        ) : null}
        {kind === 'parking' ? (
          <p className="text-adminSmall text-muted leading-normal">{t('admin.editor.parkingNote')}</p>
        ) : null}

        <div className="flex flex-col gap-3.5">
          <Labelled label={t('admin.editor.panelSize')}>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                value={object.wCm}
                onChange={(e) => onChange({ ...object, wCm: clampSize(Number(e.target.value), object.kind) })}
                className={`${fieldClass} tabular-nums`}
                aria-label={t('admin.editor.panelWidth')}
              />
              <span className="text-muted text-xs">×</span>
              <input
                type="number"
                value={object.hCm}
                onChange={(e) => onChange({ ...object, hCm: clampSize(Number(e.target.value), object.kind) })}
                className={`${fieldClass} tabular-nums`}
                aria-label={t('admin.editor.panelDepth')}
              />
            </div>
          </Labelled>
          <Labelled label={t('admin.editor.panelRotation')}>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={0}
                max={359}
                value={object.rotationDeg}
                onChange={(e) =>
                  onChange({ ...object, rotationDeg: ((Number(e.target.value) % 360) + 360) % 360 })
                }
                className={`${fieldClass} tabular-nums`}
              />
              <button
                type="button"
                aria-label={t('admin.editor.rotateLeft')}
                onClick={() => onChange(rotateObject(object, -90))}
                className="border-line rounded-input hover:bg-fog flex h-10 w-10 flex-none items-center justify-center border"
              >
                {'↺'}
              </button>
              <button
                type="button"
                aria-label={t('admin.editor.rotateRight')}
                onClick={() => onChange(rotateObject(object, 90))}
                className="border-line rounded-input hover:bg-fog flex h-10 w-10 flex-none items-center justify-center border"
              >
                {'↻'}
              </button>
            </div>
          </Labelled>
        </div>
      </div>

      {/* ------------------------------ footer ------------------------------ */}
      <div className="border-line text-muted flex items-center justify-between gap-3 border-t px-[18px] py-3 text-xs">
        <span className="flex flex-col gap-0.5">
          <span>
            {bookingsToday + bookingsUpcoming === 0
              ? t('admin.editor.bookingsNone')
              : t('admin.editor.bookingsFooter', { today: bookingsToday, upcoming: bookingsUpcoming })}
          </span>
          {/* § 6.5 — the draft lets the object go, publishing does not until
              every booking on it has a new desk (BR-PLAN-03). */}
          {bookingsUpcoming > 0 && removable ? (
            <span className="text-warn-text font-semibold">
              {t('admin.editor.removeWarning', { count: bookingsUpcoming })}
            </span>
          ) : null}
        </span>
        {/* § 6.5 — removing is allowed in the draft; the cost surfaces at
            publish. An assigned desk, office or locker cannot be removed. */}
        <button
          type="button"
          onClick={onRemove}
          disabled={!removable}
          title={removable ? undefined : t('admin.editor.removeAssigned')}
          className="text-danger-text font-body font-semibold disabled:cursor-not-allowed disabled:opacity-50"
        >
          {removeLabel}
        </button>
      </div>
    </>
  );
}

const smallFieldClass =
  'border-line-strong rounded-input text-ink h-8 w-[150px] border bg-white px-2.5 text-[13px]';

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-[5px]">
      <span className="text-label text-muted uppercase">{label}</span>
      {children}
    </div>
  );
}

/** A rule row: the name on the left, a 150 px control on the right. */
function RuleRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-ink flex items-center justify-between gap-3">
      <span>{label}</span>
      {children}
    </div>
  );
}

const LABEL_ERRORS = {
  required: 'admin.editor.labelRequired',
  too_long: 'admin.editor.labelTooLong',
  duplicate: 'admin.editor.labelDuplicate',
} as const;
